import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";

export const FormDialog = ({ open, onOpenChange, title, fields, initial = {}, onSubmit, testid }) => {
  const [values, setValues] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(initial || {});
      setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={`space-y-2 ${f.full ? "sm:col-span-2" : ""}`}>
              <Label className="overline">{f.label}</Label>
              {f.type === "select" ? (
                <Select value={values[f.name] ?? ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid={`field-${f.name}`}>
                    <SelectValue placeholder={f.placeholder || "Seleziona"} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {(f.options || []).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea
                  value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  className="rounded-sm"
                  data-testid={`field-${f.name}`}
                />
              ) : f.type === "switch" ? (
                <div className="flex items-center gap-3 h-11">
                  <Switch
                    checked={!!values[f.name]}
                    onCheckedChange={(v) => set(f.name, v)}
                    data-testid={`field-${f.name}`}
                  />
                  <span className="text-sm text-muted-foreground">{f.hint}</span>
                </div>
              ) : (
                <Input
                  type={f.type || "text"}
                  step={f.type === "number" ? "any" : undefined}
                  value={values[f.name] ?? ""}
                  onChange={(e) =>
                    set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
                  }
                  required={f.required}
                  className="h-11 rounded-sm"
                  data-testid={`field-${f.name}`}
                />
              )}
            </div>
          ))}
          {err && <p className="sm:col-span-2 text-sm text-destructive" data-testid="form-error">{err}</p>}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy} className="rounded-sm" data-testid="form-save-btn">
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ListCard = ({ children, testid }) => (
  <Card className="rounded-sm shadow-none overflow-hidden" data-testid={testid}>
    <div className="divide-y divide-border">{children}</div>
  </Card>
);

export const Empty = ({ text }) => <p className="p-6 text-sm text-muted-foreground">{text}</p>;
